// ─── Odoo RPC Configuration ─────────────────────────────────────────────────
// In development: relative URL "/jsonrpc" is proxied by CRA dev server
//   (see "proxy" in package.json).
// In production build: use the full Odoo server URL so requests reach the
//   backend regardless of where the built app is hosted.
export const DB = "mlm_platform_clientdata";
const RPC_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "https://crypto.demo.xamta.in/api/jsonrpc"
    : "/jsonrpc"; // proxied by CRA dev server

// ─── Core JSON-RPC helper ───────────────────────────────────────────────────
export async function rpc(service, method, args) {
  let response;
  try {
    response = await fetch(RPC_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        method: "call",
        params: { service, method, args },
        id: Date.now(),
      }),
    });
  } catch (networkErr) {
    throw new Error(`Network error — cannot reach Odoo server. (${networkErr.message})`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const preview = await response.text();
    throw new Error(
      `Proxy error (HTTP ${response.status}) — expected JSON but got HTML. ` +
      `Restart the dev server after adding "proxy" to package.json. ` +
      `Preview: ${preview.slice(0, 120)}`
    );
  }

  const data = await response.json();
  if (data.error) throw new Error(data.error.data?.message || data.error.message);
  return data.result;
}

// ─── Authenticate ────────────────────────────────────────────────────────────
export async function authenticate(username, password) {
  const uid = await rpc("common", "authenticate", [DB, username, password, {}]);
  if (!uid) throw new Error("Invalid Username & Password.");
  return uid;
}

// ─── Check if logged-in user is admin ───────────────────────────────────────
export async function isAdminUser(uid, password) {
  try {
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.users", "read",
      [[uid], ["login", "groups_id"]]
    ]);
    const user = records[0];
    if (!user) return false;
    // Odoo admin has login "admin" OR belongs to base.group_system
    if (user.login === "admin") return true;
    // Check for base.group_system (Settings > Technical > Administration group)
    const adminGroup = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.groups", "search",
      [[["full_name", "ilike", "Administration / Settings"]]]
    ]);
    if (adminGroup.length > 0 && user.groups_id.includes(adminGroup[0])) return true;
    return false;
  } catch {
    return false;
  }
}
export async function fetchCurrentUserInfo(uid, password) {
  try {
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.users", "read",
      [[uid], ["name", "partner_id","email"]]
    ]);
    return records[0] || null;
  } catch (e) {
    return null;
  }
}

export async function fetchCurrentUserName(uid, password) {
  const info = await fetchCurrentUserInfo(uid, password);
  return info ? info.name : "User";
}

// ─── Profile Fields ────────────────────────────────────────────────────────
export async function fetchProfile(uid, password, partnerId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "read",
    [[partnerId], ["street", "name", "street2", "city", "zip", "state_id", "country_id", "phone", "email", "mlm_sequence", "parent_id"]]
  ]);
  return records[0] || null;
}

export async function updateProfile(uid, password, partnerId, data) {
  const success = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "write",
    [[partnerId], data]
  ]);
  return success;
}

export async function setReferralByCode(uid, password, partnerId, code) {
  const result = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "set_referral_bycode",
    [[partnerId], code]
  ]);
  return result;
}

export async function updateUserEmail(uid, password, email) {
  const success = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.users", "write",
    [[uid], { email }]
  ]);
  return success;
}

// ==========================================
// TRANSACTIONS
// ==========================================
// IMPORTANT: Change "mlm.transaction" if your Odoo model has a different name!
const TRANSACTION_MODEL = "mlm.transaction";

export async function fetchTransactions(uid, password, partnerId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    TRANSACTION_MODEL, "search_read",
    [[["partner_id", "=", partnerId]]],
    { fields: ["id", "partner_id", "node_id", "trans_date", "amount", "meta", "attachment", "state"] }
  ]);
  return records || [];
}

export async function createTransaction(uid, password, data) {
  const newId = await rpc("object", "execute_kw", [
    DB, uid, password,
    TRANSACTION_MODEL, "create",
    [data]
  ]);
  return newId;
}

export async function fetchCountries(uid, password) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.country", "search_read",
    [[]],
    { fields: ["id", "name"], order: "name asc" }
  ]);
  return records;
}

export async function fetchStates(uid, password, countryId) {
  if (!countryId) return [];
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.country.state", "search_read",
    [[["country_id", "=", countryId]]],
    { fields: ["id", "name"], order: "name asc" }
  ]);
  return records;
}

export async function fetchAvailableParents(uid, password, currentPartnerId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["is_mlm_member", "=", true], ["id", "!=", currentPartnerId]]],
    { fields: ["id", "name", "mlm_sequence"], limit: 1000, order: "name asc" }
  ]);
  return records;
}

// ─── Register User via Admin Proxy ──────────────────────────────────────────
export async function registerUser(name, username, password) {
  const ADMIN_USER = "admin";
  const ADMIN_PASS = "a";

  // 1. Authenticate as admin
  const adminUid = await authenticate(ADMIN_USER, ADMIN_PASS);
  
  if (!adminUid) {
    throw new Error("Invalid admin credentials");
  }

  // 2. Create the user
  const userId = await rpc("object", "execute_kw", [
    DB,
    adminUid,
    ADMIN_PASS,
    "res.users", // Creating inside res.users automatically creates res.partner
    "create",
    [
      {
        name,
        login: username, 
        password,
        // email is usually the same as login if it's an email format, but we'll use username here
        // Set MLM member flag automatically for new users!
        is_mlm_member: true
      }
    ]
  ]);
  
  return userId;
}

// ─── MLM Fields ──────────────────────────────────────────────────────────────
export const MLM_FIELDS = [
  "id", "name", "is_mlm_member", "mlm_sequence",
  "parent_id", "post_id", "mlm_treepath",
  "total_direct_child", "total_amount", "child_total_amount",
];

// ─── Count total MLM members ─────────────────────────────────────────────────
export async function countMlmMembers(uid, password) {
  const count = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_count",
    [[["is_mlm_member", "=", true]]],
  ]);
  return count;
}

// ─── Count descendants of a specific member by treepath ──────────────────────
export async function countUserNetwork(uid, password, treepath) {
  if (!treepath) return 0;
  const count = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_count",
    [[["mlm_treepath", "like", `${treepath}%`], ["is_mlm_member", "=", true]]],
  ]);
  return count;
}

// ─── Fetch a page of a user's network descendants ────────────────────────────
// Uses mlm_treepath prefix to scope results to just this user's downline.
export async function fetchUserNetworkPage(uid, password, treepath, { offset = 0, limit = 50 } = {}) {
  if (!treepath) return [];
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["mlm_treepath", "like", `${treepath}%`], ["is_mlm_member", "=", true]]],
    {
      fields: MLM_FIELDS,
      limit,
      offset,
      order: "mlm_sequence asc, id asc",
    },
  ]);
  return records;
}

// ─── Fetch a page of MLM members ─────────────────────────────────────────────
// Returns `limit` members starting at `offset`, ordered by mlm_sequence.
export async function fetchMlmPage(uid, password, { offset = 0, limit = 50 } = {}) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["is_mlm_member", "=", true]]],
    {
      fields: MLM_FIELDS,
      limit,
      offset,
      order: "id asc",
    },
  ]);
  return records;
}

// ─── Fetch direct children of a member ───────────────────────────────────────
export async function fetchChildren(uid, password, parentId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["parent_id", "=", parentId], ["is_mlm_member", "=", true]]],
    { fields: MLM_FIELDS, limit: 200, order: "mlm_sequence asc, id asc" },
  ]);
  return records;
}

// ─── Fetch a single MLM member by ID ─────────────────────────────────────────
export async function fetchMemberById(uid, password, id) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["id", "=", id]]],
    { fields: MLM_FIELDS, limit: 1 },
  ]);
  return records[0] || null;
}

// ─── Fetch ALL descendants using mlm_treepath prefix search ──────────────────
// mlm_treepath looks like "/1/5/23/" — we search for all paths that start with
// this node's path, giving us the entire downline in one shot.
export async function fetchAllDescendants(uid, password, member) {
  // Try treepath-based search first (most efficient)
  const treepath = member.mlm_treepath;
  let domain;
  if (treepath) {
    // All descendants have mlm_treepath starting with this member's path
    domain = [
      ["mlm_treepath", "like", `${treepath}%`],
      ["is_mlm_member", "=", true],
      ["id", "!=", member.id],
    ];
  } else {
    // Fallback: BFS via parent_id
    return fetchDescendantsBFS(uid, password, member.id);
  }

  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [domain],
    { fields: MLM_FIELDS, limit: 2000, order: "mlm_sequence asc, id asc" },
  ]);
  return records;
}

// ─── BFS fallback for descendants (when treepath is missing) ──────────────────
async function fetchDescendantsBFS(uid, password, rootId) {
  const all = [];
  let queue = [rootId];
  while (queue.length > 0) {
    // Fetch children of all nodes in current queue level
    const children = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_read",
      [[["parent_id", "in", queue], ["is_mlm_member", "=", true]]],
      { fields: MLM_FIELDS, limit: 1000, order: "mlm_sequence asc" },
    ]);
    all.push(...children);
    queue = children.map(c => c.id);
  }
  return all;
}

// ─── Fetch ALL ancestors using mlm_treepath ──────────────────────────────────
export async function fetchAncestors(uid, password, members, alreadyFetchedIds = new Set()) {
  if (!Array.isArray(members)) members = [members];

  // Register characters we already have to avoid infinite loops or redundant fetches
  members.forEach(m => alreadyFetchedIds.add(m.id));

  const toFetchIds = new Set();
  members.forEach(m => {
    if (m.mlm_treepath) {
      const pathIds = m.mlm_treepath.split("/").filter(Boolean).map(Number);
      pathIds.forEach(id => {
        if (!alreadyFetchedIds.has(id)) toFetchIds.add(id);
      });
    } else if (m.parent_id && m.parent_id[0]) {
      if (!alreadyFetchedIds.has(m.parent_id[0])) toFetchIds.add(m.parent_id[0]);
    }
  });

  if (toFetchIds.size === 0) return [];

  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["id", "in", Array.from(toFetchIds)]]],
    { fields: MLM_FIELDS },
  ]);

  // Recursively fetch ancestors of ancestors (in case treepath was missing for some)
  const more = await fetchAncestors(uid, password, records, alreadyFetchedIds);
  return [...records, ...more];
}

// ─── Build hierarchy from a flat list ────────────────────────────────────────
// Members whose parent is NOT in the list become roots.
export function buildHierarchy(records) {
  // Sort ALL records by depth (path length) then sequence/ID.
  // This ensures that parents are processed before children (if possible)
  // and that children are added to the parent's array in the correct order.
  const sorted = [...records].sort((a, b) => {
    const lenA = (a.mlm_treepath || "").split("/").length;
    const lenB = (b.mlm_treepath || "").split("/").length;
    if (lenA !== lenB) return lenA - lenB;
    return (a.mlm_sequence || 0) - (b.mlm_sequence || 0) || a.id - b.id;
  });

  const map = {};
  const roots = [];

  sorted.forEach(r => { map[r.id] = { ...r, children: [] }; });
  sorted.forEach(r => {
    const pid = r.parent_id ? r.parent_id[0] : null;
    if (pid && map[pid]) map[pid].children.push(map[r.id]);
    else roots.push(map[r.id]);
  });

  return roots;
}

// ─── Assign depth field recursively ──────────────────────────────────────────
export function computeDepths(nodes, depth = 0) {
  return nodes.map(n => ({
    ...n, depth,
    children: computeDepths(n.children, depth + 1),
  }));
}